import { FC, PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { PlanFeatureCategory, PlanFeaturesMetadata } from '@/features/common/pricing';

import { CategoryFeaturesMetadataBuilder } from './Builder';
import { PlanFeaturesMetadataContext } from './Context';
import { IProductMetadata } from '@/features/pricingV2/domain';
import {
  mapAmountOfCredits,
  mapBulkSave,
  mapCcpaCompliant,
  mapDashboard,
  mapDedicatedSupport,
  mapEmailSupport,
  mapEnrichCsv,
  mapExportCredits,
  mapExportToCsv,
  mapGdprCompliant,
  mapGoogle,
  mapHelpCenter,
  mapLinkedinExt,
  mapListManagement,
  mapLiveChat,
  mapMicrosoft,
  mapOkta,
  mapOnboarding,
  mapSaml,
  mapSaveToCrm,
  mapSaveToCrmComingSoon,
  mapTagManagement,
  mapTeamUsageAnalytics,
  mapTopUpCredits,
  mapUniversalExt,
  mapUsageAnalytics,
} from './Mapper';

export const PlanFeaturesMetadataProvider: FC<
  PropsWithChildren<{
    productsMetadata: IProductMetadata[];
  }>
> = ({ children, productsMetadata }) => {
  const { t } = useTranslation('pricing');

  const planFeaturesMetadata: PlanFeaturesMetadata = useMemo(() => {
    const builder = (category: PlanFeatureCategory): CategoryFeaturesMetadataBuilder => {
      return new CategoryFeaturesMetadataBuilder().category(category).t(t);
    };

    return {
      [PlanFeatureCategory.General]: builder(PlanFeatureCategory.General)
        .feature('amountOfCredits', mapAmountOfCredits(productsMetadata))
        .feature('exportCredits', mapExportCredits(productsMetadata))
        .feature('linkedinExt', mapLinkedinExt(productsMetadata))
        .feature('universalExt', mapUniversalExt(productsMetadata))
        .feature('dashboard', mapDashboard(productsMetadata))
        .feature('topUpCredits', mapTopUpCredits(productsMetadata))
        .feature('bulkSave', mapBulkSave(productsMetadata))
        .feature('enrichCsv', mapEnrichCsv(productsMetadata))
        .build(),
      [PlanFeatureCategory.ContactManagement]: builder(
        PlanFeatureCategory.ContactManagement,
      )
        .feature('listManagement', mapListManagement(productsMetadata))
        .feature('tagManagement', mapTagManagement(productsMetadata))
        .feature('exportToCsv', mapExportToCsv(productsMetadata))
        .feature('saveToCrm', mapSaveToCrm(productsMetadata))
        .feature('saveToSalesEngagementTool', mapSaveToCrm(productsMetadata))
        .build(),
      [PlanFeatureCategory.CrmIntegrations]: builder(PlanFeatureCategory.CrmIntegrations)
        .feature('salesforce', mapSaveToCrm(productsMetadata))
        .feature('hubspot', mapSaveToCrm(productsMetadata))
        .feature('zohoCrm', mapSaveToCrm(productsMetadata))
        .feature('pipedrive', mapSaveToCrmComingSoon(productsMetadata))
        .feature('zapier', mapSaveToCrmComingSoon(productsMetadata))
        .feature('outreach', mapSaveToCrmComingSoon(productsMetadata))
        .feature('salesloft', mapSaveToCrmComingSoon(productsMetadata))
        .feature('customIntegration', mapSaveToCrm(productsMetadata))
        .build(),
      [PlanFeatureCategory.Analytics]: builder(PlanFeatureCategory.Analytics)
        .feature('usageAnalytics', mapUsageAnalytics(productsMetadata))
        .feature('teamUsageAnalytics', mapTeamUsageAnalytics(productsMetadata))
        .build(),
      [PlanFeatureCategory.Compliance]: builder(PlanFeatureCategory.Compliance)
        .feature('gdprCompliant', mapGdprCompliant(productsMetadata))
        .feature('ccpaCompliant', mapCcpaCompliant(productsMetadata))
        .build(),
      [PlanFeatureCategory.SSO]: builder(PlanFeatureCategory.SSO)
        .feature('google', mapGoogle(productsMetadata))
        .feature('microsoft365', mapMicrosoft(productsMetadata))
        .feature('okta', mapOkta(productsMetadata))
        .feature('saml', mapSaml(productsMetadata))
        .build(),
      [PlanFeatureCategory.Support]: builder(PlanFeatureCategory.Support)
        .feature('helpCenter', mapHelpCenter(productsMetadata))
        .feature('emailSupport', mapEmailSupport(productsMetadata))
        .feature('liveChat', mapLiveChat(productsMetadata))
        .feature('onboarding', mapOnboarding(productsMetadata))
        .feature('dedicatedSupport', mapDedicatedSupport(productsMetadata))
        .build(),
    };
  }, [t, productsMetadata]);

  return (
    <PlanFeaturesMetadataContext.Provider value={planFeaturesMetadata}>
      {children}
    </PlanFeaturesMetadataContext.Provider>
  );
};
